import { getFileExtension, getFileKind, type FileKind } from '@/shared/lib/files';

import type { Breadcrumb, DiskItem, DiskRole, ItemDetails, Storage } from '../api';

/*
 * Pure helpers around disk items: routes, roles, previews (unit tested).
 */

const ROLE_ORDER: Record<DiskRole, number> = { none: 0, view: 1, edit: 2, manage: 3 };

/** `role` includes `required` (manage ⊃ edit ⊃ view). */
export const hasRole = (role: DiskRole, required: DiskRole): boolean =>
  ROLE_ORDER[role] >= ROLE_ORDER[required];

export const isFolder = (item: Pick<DiskItem, 'type'>): boolean => item.type === 'folder';

// ---- routes (MODULE-CONTRACTS §2) -----------------------------------------------------------------------------------

export const DISK_ROOT = '/disk';
export const folderPath = (folderId: string): string => `/disk/folders/${folderId}`;
export const groupDrivePath = (groupId: string): string => `/disk/groups/${groupId}`;
export const filePath = (itemId: string): string => `/disk/files/${itemId}`;
export const trashPath = (storageId?: string): string =>
  storageId ? `/disk/trash?storage=${storageId}` : '/disk/trash';
export const panelSearch = (itemId: string): string => `?panel=file:${encodeURIComponent(itemId)}`;

/** The page of a drive's root: `/disk`, `/disk/company`, `/disk/groups/<id>`. */
export function storagePath(
  storage: Pick<Storage, 'kind' | 'group' | 'rootFolderId'>,
  myStorageId?: string,
): string {
  if (storage.kind === 'company') return '/disk/company';
  if (storage.kind === 'group' && storage.group) return groupDrivePath(storage.group.id);
  if (storage.kind === 'user' && (!myStorageId || myStorageId === (storage as Storage).id)) return DISK_ROOT;
  return folderPath(storage.rootFolderId);
}

/** Where an item lives for the caller: the folder to open it in, `null` when it is reachable only by itself (a shared file). */
export function containingFolderId(details: Pick<ItemDetails, 'item' | 'path'>): string | null {
  const ids = details.path.map((p) => p.id);
  const own = ids.lastIndexOf(details.item.id);
  const above = own >= 0 ? ids.slice(0, own) : ids;
  return above[above.length - 1] ?? null;
}

/** The SPA location that shows an item: its folder with the file panel open (or the folder itself). */
export function itemLocation(details: Pick<ItemDetails, 'item' | 'path'>): string {
  if (isFolder(details.item)) return folderPath(details.item.id);
  const folder = containingFolderId(details);
  return `${folder ? folderPath(folder) : '/disk/shared'}${panelSearch(details.item.id)}`;
}

/**
 * Breadcrumbs below the drive root: the root folder is shown as the drive's name, so it is dropped from a path that
 * starts at the drive root (it has an empty name); a path through a share starts at the shared folder and keeps it.
 */
export function visiblePath(path: readonly Breadcrumb[]): Breadcrumb[] {
  return path.filter((crumb, index) => !(index === 0 && crumb.name === ''));
}

// ---- previews -------------------------------------------------------------------------------------------------------

export type PreviewKind = 'image' | 'pdf' | 'video' | 'audio' | 'markdown' | 'csv' | 'text' | 'none';

const TEXT_EXTENSIONS = new Set([
  'txt',
  'log',
  'json',
  'xml',
  'yaml',
  'yml',
  'sh',
  'sql',
  'ini',
  'conf',
  'cs',
  'ts',
  'js',
  'css',
  'html',
]);
const BROWSER_IMAGES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/avif',
  'image/svg+xml',
]);

/** Largest text file previewed in the panel (bigger ones offer a download). */
export const MAX_TEXT_PREVIEW_BYTES = 2 * 1024 * 1024;

/** How the side panel previews a file. */
export function previewKind(item: Pick<DiskItem, 'name' | 'contentType' | 'size' | 'type'>): PreviewKind {
  if (item.type === 'folder') return 'none';
  const type = item.contentType?.toLowerCase() ?? '';
  const extension = getFileExtension(item.name);
  if (type.startsWith('image/'))
    return BROWSER_IMAGES.has(type) && type !== 'image/svg+xml' ? 'image' : 'none';
  if (type === 'application/pdf' || extension === 'pdf') return 'pdf';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  const textual =
    extension === 'md' ||
    extension === 'markdown' ||
    extension === 'csv' ||
    TEXT_EXTENSIONS.has(extension) ||
    type.startsWith('text/');
  if (!textual) return 'none';
  if (item.size > MAX_TEXT_PREVIEW_BYTES) return 'none';
  if (extension === 'md' || extension === 'markdown' || type === 'text/markdown') return 'markdown';
  if (extension === 'csv' || type === 'text/csv') return 'csv';
  return 'text';
}

/** Kind used for icons and filters (shared classification). */
export const fileKindOf = (item: Pick<DiskItem, 'name' | 'contentType'>): FileKind =>
  getFileKind(item.name, item.contentType);

/** 256 px thumbnail of an image, or `size` px (64 | 1024) — the files module serves those from the same signed URL. */
export function thumbnailUrl(item: Pick<DiskItem, 'file'>, size?: 64 | 1024): string | null {
  const url = item.file?.thumbnailUrl;
  if (!url) return null;
  return size ? `${url}${url.includes('?') ? '&' : '?'}size=${size}` : url;
}

/** Items sorted like the server does when it sorts by name (folders first) — for optimistic inserts. */
export function compareByName(
  a: Pick<DiskItem, 'type' | 'name'>,
  b: Pick<DiskItem, 'type' | 'name'>,
): number {
  if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true });
}

/** Days left until an item in the trash is purged (0 = today). */
export function daysUntil(iso: string, now = Date.now()): number {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - now) / 86_400_000));
}

/** Share of the quota in percent (0–100, one decimal). */
export function usagePercent(used: number, quota: number): number {
  if (quota <= 0) return 0;
  return Math.min(100, Math.round((used / quota) * 1000) / 10);
}

/** Lifetimes offered for public links (days; `never` = until removed). */
export const EXPIRY_OPTIONS = ['never', '1', '7', '30', '90'] as const;
export type ExpiryOption = (typeof EXPIRY_OPTIONS)[number];

/** The expiry sent for an option: `null` for none, else that many days from now. */
export const expiryFromOption = (option: ExpiryOption, now = Date.now()): string | null =>
  option === 'never' ? null : new Date(now + Number(option) * 86_400_000).toISOString();
